import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { handleLogout } from "../Services/APIService";
import { usePasswordModal } from "../contexts/PasswordModalContext";
import { useNotifications } from "../contexts/NotificationContext";
import { useManagerModal } from "../contexts/ManagerModalContext";
import { useUserModal } from "../contexts/UserModalContext";
import "../css/notification.css";

interface NavbarProps {
  onToggleSidebar: () => void;
}

const ROLE_ADMIN = 1;
const ROLE_MANAGER = 2;
const ROLE_LEAD_ADMIN = 3;
const ROLE_USER = 4;
const ROLE_SALES_COORDINATOR = 5;
const ROLE_CHANNEL_PARTNER = 6;

const Navbar: React.FC<NavbarProps> = ({ onToggleSidebar }) => {
  const navigate = useNavigate();
  const { openPasswordModal } = usePasswordModal();
  const { openManagerModal } = useManagerModal();
  const { openUserModal } = useUserModal();
  const { notifications, unreadCount, markAsRead, markAllAsRead, isConnected } =
    useNotifications();

  const [userFullName, setUserFullName] = useState<string>("User");
  const [userId, setUserId] = useState<number>(0);
  const [userTypeId, setUserTypeId] = useState<number>(0);
  const [hiddenNotifications, setHiddenNotifications] = useState<Set<number>>(new Set());
  const [isImpersonating, setIsImpersonating] = useState<boolean>(
    localStorage.getItem("isImpersonating") === "true"
  );
  const [isLoginAsManager, setIsLoginAsManager] = useState<boolean>(
    localStorage.getItem("isLoginAsManager") === "true"
  );

  useEffect(() => {
    const storedUserName = localStorage.getItem("userFullName");
    const storedUserId = localStorage.getItem("UserId");
    const storedUserTypeId = localStorage.getItem("userTypeId");
    if (storedUserName) setUserFullName(storedUserName);
    if (storedUserId) setUserId(parseInt(storedUserId, 10));
    if (storedUserTypeId) setUserTypeId(parseInt(storedUserTypeId, 10));
  }, []);

  const onLogout = (e: React.MouseEvent) => {
    e.preventDefault();
    localStorage.removeItem("isImpersonating");
    localStorage.removeItem("originalSession");
    localStorage.removeItem("isLoginAsManager");
    handleLogout(navigate);
  };

  const canLoginAsManager =
    userTypeId === ROLE_ADMIN || userTypeId === ROLE_LEAD_ADMIN;
  const canLoginAsUser = userTypeId === ROLE_MANAGER;

  const handleBackToOriginal = (e: React.MouseEvent) => {
    e.preventDefault();
    const raw = localStorage.getItem("originalSession");
    if (!raw) return;
    const original = JSON.parse(raw);
    Object.entries(original).forEach(([key, value]) => {
      if (value) localStorage.setItem(key, value as string);
    });
    localStorage.removeItem("originalSession");
    localStorage.removeItem("isImpersonating");
    localStorage.removeItem("isLoginAsManager");
    setIsImpersonating(false);
    setIsLoginAsManager(false);
    setUserFullName(original.userFullName || "User");
    setUserId(parseInt(original.UserId || "0", 10));
    setUserTypeId(parseInt(original.userTypeId || "0", 10));
    window.location.replace(`/#/dashboard`);
    window.location.reload();
  };

  const handleSidebarToggle = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    const currentState = localStorage.getItem("isMobileToggled");
    if (currentState === "true") {
      localStorage.removeItem("isMobileToggled");
    } else {
      localStorage.setItem("isMobileToggled", "true");
    }
  };

  // ✅ Fixed: stopPropagation + new Set() for React to detect change
  const handleNotificationClick = async (
    notificationId: number,
    e: React.MouseEvent
  ) => {
    e.preventDefault();
    e.stopPropagation(); // ✅ prevents parent div onClick from firing

    setHiddenNotifications((prev) => {
      const newSet = new Set(prev); // ✅ new Set so React detects change
      newSet.add(notificationId);
      return newSet;
    });

    await markAsRead(notificationId);
  };

  const handleMarkAllAsRead = (e: React.MouseEvent) => {
    e.preventDefault();
    const visibleNotificationIds = userNotifications
      .filter((n) => !hiddenNotifications.has(n.id))
      .map((n) => n.id);
    setHiddenNotifications((prev) => {
      const newSet = new Set(prev);
      visibleNotificationIds.forEach((id) => newSet.add(id));
      return newSet;
    });
    markAllAsRead();
  };

  const getPriorityClass = (priority: string): string => {
    switch (priority?.toLowerCase()) {
      case "high": return "text-danger";
      case "medium": return "text-warning";
      case "low": return "text-info";
      default: return "text-muted";
    }
  };

  const handleShowAllNotifications = (e: React.MouseEvent) => {
    e.preventDefault();
    console.log("Show all notifications clicked");
  };

  // ✅ useMemo to avoid recalculating on every render
  const userNotifications = useMemo(
    () =>
      notifications.filter(
        (n) =>
          n.metadata?.assignedUserId === userId.toString() &&
          !hiddenNotifications.has(n.id)
      ),
    [notifications, hiddenNotifications, userId]
  );

  const userUnreadCount = useMemo(
    () => userNotifications.filter((n) => !n.isRead).length,
    [userNotifications]
  );

  const leadDashbordRedirection = async (leadId: number) => {
    try {
      navigate("/LeadDashboard", { state: { leadId } });
    } catch (error) {
      console.error("Error redirecting to lead details:", error);
    }
  };

  return (
    <nav className="navbar navbar-expand navbar-light navbar-bg">

      <a
        href="#"
        className="sidebar-toggle js-sidebar-toggle d-none d-sm-block"
        onClick={(e: React.MouseEvent<HTMLAnchorElement>) => {
          e.preventDefault();
          onToggleSidebar();
        }}
      >
        <i className="bi bi-fullscreen text-white"></i>
      </a>

      <a
        href="#"
        className="sidebar-toggle js-sidebar-toggle d-block d-sm-none"
        onClick={handleSidebarToggle}
      >
        <i className="bi bi-three-dots-vertical"></i>
      </a>

      <div className="navbar-collapse collapse">
        <ul className="navbar-nav navbar-align">

          {/* ========== MANAGER LOGIN BADGE ========== */}
          {isLoginAsManager && userTypeId === ROLE_MANAGER && (
            <li className="nav-item d-flex align-items-center me-2">
              <span
                style={{
                  background: "#E6F1FB",
                  border: "1px solid #85B7EB",
                  borderRadius: "99px",
                  padding: "3px 10px",
                  display: "flex",
                  alignItems: "center",
                  gap: "5px",
                  fontSize: "11px",
                  fontWeight: 500,
                  color: "#0C447C",
                  whiteSpace: "nowrap",
                }}
              >
                <span
                  style={{
                    width: "7px",
                    height: "7px",
                    borderRadius: "50%",
                    background: "#185FA5",
                    display: "inline-block",
                  }}
                />
                Now in Manager Login
              </span>
            </li>
          )}

          {/* ========== NOTIFICATIONS ========== */}
          <li className="nav-item dropdown">
            <a
              href="#"
              className="nav-icon dropdown-toggle position-relative"
              id="alertsDropdown"
              data-bs-toggle="dropdown"
            >
              <div className="position-relative">
                <i className="bi bi-bell align-middle text-white"></i>
                {userUnreadCount > 0 && (
                  <span className="indicator">
                    {userUnreadCount > 99 ? "99+" : userUnreadCount}
                  </span>
                )}
                <span
                  className={`position-absolute top-0 start-100 translate-middle p-1 ${
                    isConnected ? "bg-success" : "bg-danger"
                  } border border-light rounded-circle`}
                  style={{ fontSize: "6px", zIndex: 1000 }}
                  title={isConnected ? "Connected" : "Disconnected"}
                >
                  <span className="visually-hidden">
                    {isConnected ? "Connected" : "Disconnected"}
                  </span>
                </span>
              </div>
            </a>

            <div
              className="dropdown-menu dropdown-menu-lg dropdown-menu-end p-0 premium-notification-dropdown"
              aria-labelledby="alertsDropdown"
            >
              {/* ===== HEADER ===== */}
              <div className="premium-notification-header d-flex justify-content-between align-items-center px-3 py-2">
                <div>
                  <strong className="text-white">Notifications</strong>
                  <div className="small text-light opacity-75">
                    {userUnreadCount > 0
                      ? `${userUnreadCount} Unread`
                      : "You're all caught up 🎉"}
                  </div>
                </div>
                {userUnreadCount > 0 && (
                  <button
                    className="btn btn-sm btn-light fw-semibold"
                    onClick={handleMarkAllAsRead}
                  >
                    Mark all
                  </button>
                )}
              </div>

              {/* ===== BODY ===== */}
              <div className="premium-notification-body">
                {userNotifications.length === 0 ? (
                  <div className="text-center text-muted py-4">
                    No notifications available
                  </div>
                ) : (
                  userNotifications.map((notification) => (
                    <div
                      key={notification.id}
                      className={`premium-notification-item ${
                        !notification.isRead ? "unread" : ""
                      }`}
                      onClick={() => {
                        if (notification.metadata?.leadId) {
                          leadDashbordRedirection(notification.metadata.leadId);
                        }
                      }}
                    >
                      <div className="d-flex gap-3 align-items-start">
                        <div className="premium-icon">
                          <i
                            className={`${notification.icon} ${getPriorityClass(
                              notification.priority
                            )}`}
                          ></i>
                        </div>

                        <div className="flex-grow-1">
                          <div className="notification-title">
                            {notification.title}
                          </div>
                          <div className="text-muted small mt-1">
                            {notification.description}
                          </div>
                          {notification.metadata && (
                            <div className="text-muted small mt-1">
                              <i className="bi bi-person me-1"></i>
                              {notification.metadata.leadName}
                              {notification.metadata.projectName && (
                                <>
                                  {" | "}
                                  <i className="bi bi-building me-1"></i>
                                  {notification.metadata.projectName}
                                </>
                              )}
                            </div>
                          )}
                          <div className="small text-muted mt-1 d-flex align-items-center gap-2">
                            <i className="bi bi-clock"></i>
                            {notification.time}
                            {notification.priority && (
                              <span className="badge premium-priority">
                                {notification.priority}
                              </span>
                            )}
                          </div>
                        </div>

                        <button
                          className="premium-close-btn"
                          onClick={(e) =>
                            handleNotificationClick(notification.id, e)
                          }
                        >
                          ×
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </li>

          {/* ========== USER DROPDOWN ========== */}
          <li className="nav-item dropdown">
            <a
              href="#"
              className="nav-icon dropdown-toggle d-inline-block d-sm-none"
              data-bs-toggle="dropdown"
            >
              <i className="align-middle" data-feather="settings"></i>
            </a>

            <a
              href="#"
              className="nav-link dropdown-toggle d-none d-sm-inline-block text-white"
              data-bs-toggle="dropdown"
            >
              <img
                src="img/avatars/user.png"
                className="avatar rounded-circle img-fluid rounded me-1"
                alt={userFullName}
              />
              <span className="text-dark">{userFullName}</span>
            </a>

            <div className="dropdown-menu p-2 dropdown-menu-end profileDropdown">

              {isImpersonating && (
                <button
                  onClick={handleBackToOriginal}
                  className="dropdown-item text-primary fw-bold"
                >
                  <i className="bi bi-arrow-return-left me-1"></i>
                  Back to Original Login
                </button>
              )}

              {isImpersonating && <hr className="dropdown-divider my-1" />}

              <button
                onClick={openPasswordModal}
                className="dropdown-item navdrpdowna-icon"
              >
                <i className="bi bi-gear navdrpdown-icon align-middle"></i>{" "}
                Change Password
              </button>

              {canLoginAsManager && (
                <button onClick={openManagerModal} className="dropdown-item">
                  <i className="bi bi-box-arrow-in-right me-1"></i>
                  Login as User
                </button>
              )}

              {canLoginAsUser && (
                <button onClick={openUserModal} className="dropdown-item">
                  <i className="bi bi-person me-1"></i>
                  Login as...
                </button>
              )}

              <a
                href="#"
                className="dropdown-item navdrpdowna-icon"
                onClick={onLogout}
              >
                <i className="bi bi-box-arrow-right navdrpdown-icon align-middle"></i>
                Log out
              </a>
            </div>
          </li>

        </ul>
      </div>
    </nav>
  );
};

export default Navbar;